import React, { useState, useEffect } from "react";
import { useCart } from "../../contexts/Cart/CartContext";
import { useNavigate } from "react-router-dom";
import { ShippingInfo } from "../../Interfaces/ShippingInfo";
import config from "../../config";
import {
  SHIP_TO_CUSTOMER,
  SHIP_TO_ORG,
  SHIP_TO_PARTICIPANT,
} from "../../CommonFunctions";
import { FundraiserResponse } from "../../Interfaces/Fundraiser";
import apiClient from "../../services/apiClient";
import { usePopup } from "../../components/Popup/PopupContext";
import PopupModel from "../../components/Popup/PopupModel";
import { ContactInfo } from "../../components/Checkout/OrgShippingInfoForm";

const BASE_API = config.API_BASE_URL;

interface OrderSummaryComponentProps {
  freeShippingThreshold: number;
  baseShippingCost: number;
  isCheckoutEnabled?: boolean; // enables or disables the "checkout" button
  setIsCheckoutEnabled?: (value: boolean) => void;
  onShippingStep?: boolean; // signal to say that this is called from the checkout page
  shippingData?: ShippingInfo; // contains shipping info for checkout phase only
  freeShippingOption?: boolean;
  shippingOption?: string;
  fundraiser?: FundraiserResponse;
  contactInfo?: ContactInfo;
}

const OrderSummaryComponent: React.FC<OrderSummaryComponentProps> = ({
  freeShippingThreshold,
  baseShippingCost,
  isCheckoutEnabled = true,
  setIsCheckoutEnabled = null,
  onShippingStep = false,
  shippingData = null,
  freeShippingOption = false,
  shippingOption = "",
  fundraiser,
  contactInfo = null,
}) => {
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeMessage, setPromoCodeMessage] = useState({
    message: "",
    isError: false,
  });
  const { state, subtotal, totalItems } = useCart();
  const { cart } = state;
  const [url, setUrl] = useState<string>("");
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();
  let shippingCost = 0;
  const DONATION_ID = config.DONATION_ITEM_ID;
  const BUYOUT_ID = config.BUYOUT_ITEM_ID;
  const [allDonoItems, setAllDonoItems] = useState(false);
  const [waitForContactForm, setWaitForContactForm] = useState(false);

  const handlePromoCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPromoCode(event.target.value);
  };

  const applyPromoCode = () => {
    setPromoCodeMessage({ message: "Promo code not found!", isError: true });
    // Handle promo code application logic here
  };

  const beginCheckout = () => {
    if (totalItems === 0 || !cart || subtotal <= 0) {
      console.warn(
        "Cannot proceed to checkout: Cart is empty or subtotal is invalid."
      );
      return;
    }
    if (onShippingStep) {
      if (url === "") {
        console.error("Invalid URL, cannot proceed to checkout");
        return;
      }
      window.location.href = url;
    } else {
      navigate("/checkout/");
    }
  };

  const handleShippingChoiceChangeBeforeCheckout = () => {
    if (shippingOption === SHIP_TO_CUSTOMER || waitForContactForm) {
      // call get stripe page if we are shipping to customer
      // or if we have gotten confirmation that we can ship to org but
      // are waiting on contact form to be filled out
      getShipping();
      getStripePage();
    } else if (shippingOption === SHIP_TO_PARTICIPANT) {
      // not implemented yet
      throw new Error("Not Implemented Yet");
    } else if (shippingOption === SHIP_TO_ORG && !waitForContactForm) {
      // make a popup
      const formattedAddress = (
        <div style={{ marginTop: "10px", marginBottom: "10px" }}>
          <div>{fundraiser?.org_shipping_info?.street_address || ""}</div>
          {fundraiser?.org_shipping_info?.apt_suite && (
            <div>{fundraiser?.org_shipping_info?.apt_suite}</div>
          )}
          <div>
            {fundraiser?.org_shipping_info?.city || ""},{" "}
            {fundraiser?.org_shipping_info?.state || ""}{" "}
            {fundraiser?.org_shipping_info?.zip_code || ""}
          </div>
        </div>
      );
      const checkInfoAndContinuePopup = new PopupModel(
        "Wait!",
        (
          <div>
            You selected ship to organization, meaning that your order will be
            delivered at the <strong>end</strong> of the fundraiser to{" "}
            {state.fundraiser_participant?.full_name ||
              "your selected participant"}
            . You will be able to collect your items from{" "}
            {state.fundraiser_participant?.full_name ||
              "your selected participant"}{" "}
            <strong>2-3 weeks</strong> after the store closes.
          </div>
        ),
        [
          {
            label: "I agree, continue",
            onClick: () => {
              getStripePage();
              // if (setIsCheckoutEnabled) {
              //   setIsCheckoutEnabled(true);
              // }
              setWaitForContactForm(true);
              hidePopup();
            },
          },
          {
            label: "Select another shipping option",
            onClick: () => {
              if (setIsCheckoutEnabled) {
                setIsCheckoutEnabled(false);
              }
              setWaitForContactForm(false);
              hidePopup();
            },
          },
        ],
        "38vw"
      );

      showPopup(
        checkInfoAndContinuePopup.header,
        checkInfoAndContinuePopup.body,
        checkInfoAndContinuePopup.buttons,
        checkInfoAndContinuePopup.width
      );
      if (setIsCheckoutEnabled) {
        setIsCheckoutEnabled(false);
      }
    }
  };

  const getStripePage = async () => {
    if (!contactInfo?.full_name || !contactInfo?.email) {
      return;
    }
    const donation = cart.reduce((sum, item) => {
      if (item.sku === DONATION_ID || item.sku === BUYOUT_ID) {
        return sum + item.price * item.quantity;
      }
      return sum;
    }, 0);
    const price = subtotal - donation;
    // Initialize the shippingInfo object
    let shippingInfo: ShippingInfo = {
      ship_to_name: "",
      street_address: "",
      city: "",
      state: "",
      zip_code: "",
      apt_suite: "",
    };

    // Update shippingInfo based on shippingOption
    if (shippingOption === SHIP_TO_CUSTOMER) {
      shippingInfo = {
        ship_to_name: shippingData?.ship_to_name || "",
        street_address: shippingData?.street_address || "",
        city: shippingData?.city || "",
        state: shippingData?.state || "",
        zip_code: shippingData?.zip_code || "",
        apt_suite: shippingData?.apt_suite || "",
      };
    } else if (shippingOption === SHIP_TO_PARTICIPANT) {
      throw new Error("Not Implemented Yet");
    } else if (shippingOption === SHIP_TO_ORG) {
      shippingInfo = {
        ship_to_name: fundraiser?.org_shipping_info?.ship_to_name || "",
        street_address: fundraiser?.org_shipping_info?.street_address || "",
        city: fundraiser?.org_shipping_info?.city || "",
        state: fundraiser?.org_shipping_info?.state || "",
        zip_code: fundraiser?.org_shipping_info?.zip_code || "",
        apt_suite: fundraiser?.org_shipping_info?.apt_suite || "",
      };
    }

    if (
      !shippingInfo?.ship_to_name ||
      !shippingInfo?.street_address ||
      !shippingInfo?.city ||
      !shippingInfo?.state ||
      !shippingInfo?.zip_code
    ) {
      // If its all dono then we dont need a shipping address
      if (!allDonoItems) {
        return;
      }
    }
    try {
      const response = await apiClient.post(
        `${BASE_API}checkout/check-taxation-info/square/`,
        {
          shippingData: shippingInfo,
          shipping: shippingCost,
          cart,
        }
      );
      // before navigating to the page, save the necessary info for checkout success
      localStorage.setItem("shippingInfo", JSON.stringify(shippingInfo));
      localStorage.setItem("shippingOption", shippingOption);
      localStorage.setItem("squareOrderID", response.data.orderID);
      localStorage.setItem("customerName", contactInfo?.full_name || "");
      localStorage.setItem("customerEmail", contactInfo?.email || "");
      localStorage.setItem("customerPhoneNumber", contactInfo?.phone || "");

      setUrl(response.data.url);
    } catch (error) {
      console.error("Error during checkout:", error);
    }
  };

  useEffect(() => {
    if (shippingOption === SHIP_TO_CUSTOMER && setIsCheckoutEnabled) {
      // if shipping option gets changed to ship to customer, turn off checkout and url
      // just in case user switched from org to customer, don't want info to stay
      setIsCheckoutEnabled(false);
      setUrl("");
      // then try getting the stripe page, in case they set their shipping info
      getShipping();
      getStripePage();
    }
  }, [shippingOption]);

  useEffect(() => {
    handleShippingChoiceChangeBeforeCheckout();
  }, [shippingData, shippingOption, contactInfo]);

  useEffect(() => {
    if (url !== "" && setIsCheckoutEnabled) {
      setIsCheckoutEnabled(true);
    }
  }, [url]);

  const getShipping = () => {
    const donation = cart.reduce((sum, item) => {
      if (item.sku === DONATION_ID || item.sku === BUYOUT_ID) {
        return sum + item.price * item.quantity;
      }
      return sum;
    }, 0);
    const donation_items = cart.reduce((sum, item) => {
      if (item.sku === DONATION_ID || item.sku === BUYOUT_ID) {
        return sum + item.quantity;
      }
      return sum;
    }, 0);
    const price = subtotal - donation;
    const non_dono_items = totalItems - donation_items;
    if (
      price >= freeShippingThreshold ||
      freeShippingOption ||
      non_dono_items === 0
    ) {
      freeShippingOption = true;
      shippingCost = 0;
      return "Free";
    }
    freeShippingOption = false;
    shippingCost = baseShippingCost + non_dono_items;
    return `$${shippingCost ? shippingCost.toFixed(2) : "0.00"}`;
  };

  useEffect(() => {
    if (allDonoItems) {
      getStripePage();
    }
  }, [allDonoItems]);

  const skipCheckoutIfAllDonoOrBuyout = () => {
    const donation_items = cart.reduce((sum, item) => {
      if (item.sku === DONATION_ID || item.sku === BUYOUT_ID) {
        return sum + item.quantity;
      }
      return sum;
    }, 0);
    const non_dono_items = totalItems - donation_items;
    if (non_dono_items === 0) {
      freeShippingOption = true;
      shippingCost = 0;
      // skip to stripe
      setAllDonoItems(true);
    }
  };

  useEffect(() => {
    if (onShippingStep) {
      skipCheckoutIfAllDonoOrBuyout();
    }
  }, []);

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>Order Summary</h2>
      <div style={styles.field}>
        <span>Item Subtotal:</span>
        <span>${subtotal.toFixed(2)}</span>
      </div>
      <hr style={styles.separator} />
      <div style={styles.promoContainer}>
        <input
          type="text"
          value={promoCode}
          onChange={handlePromoCodeChange}
          placeholder="Enter promo code"
          style={styles.promoInput}
        />
        <button onClick={applyPromoCode} style={styles.applyButton}>
          Apply
        </button>
        <p
          style={{
            color: promoCodeMessage.isError ? "red" : "green",
            textAlign: "center",
          }}
        >
          {promoCodeMessage.message}
        </p>
      </div>
      <button
        onClick={beginCheckout}
        style={
          isCheckoutEnabled
            ? styles.checkoutButton
            : { ...styles.checkoutButton, ...styles.checkoutButtonDisabled }
        }
        disabled={!isCheckoutEnabled}
      >
        Checkout
      </button>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    border: "1px solid #ccc",
    borderRadius: "5px",
    padding: "20px",
    width: "100%",
    fontFamily: "Arial, sans-serif",
    maxWidth: "450px",
    boxSizing: "border-box",
  },
  header: {
    margin: "0 0 15px 0",
  },
  field: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "10px",
    flexWrap: "wrap" as "wrap", // Explicitly cast the value
  },
  separator: {
    margin: "10px 0",
    border: "0",
    borderTop: "1px solid #ccc",
  },
  promoContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: "15px",
    flexWrap: "wrap" as "wrap", // Explicitly cast the value
    justifyContent: "center",
  },
  promoInput: {
    flex: 1,
    padding: "8px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    marginRight: "10px",
    minWidth: "calc(100% - 100px)",
  },
  applyButton: {
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    padding: "8px 12px",
    cursor: "pointer",
    width: "80px",
  },
  checkoutButton: {
    backgroundColor: "black",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    padding: "10px 20px",
    cursor: "pointer",
    width: "100%",
  },
  checkoutButtonDisabled: {
    backgroundColor: "#d3d3d3",
    color: "#808080",
    border: "none",
    borderRadius: "4px",
    padding: "10px 20px",
    cursor: "not-allowed",
    width: "100%",
    opacity: 0.6,
  },
};

export default OrderSummaryComponent;
